/**
 * 把畫面上的單據存成 JPEG 圖片(REQ-20260903006)。
 *
 * **PDF 那條路一行都沒動。** 另存 PDF 仍然走瀏覽器列印(`window.print()`),
 * 這是 REQ-20260806003 起的既有決策(不引 jsPDF / html2pdf / pdfkit / puppeteer)。
 * 但瀏覽器列印**只能**輸出 PDF,做不出 JPEG —— 需求要的第二種格式沒有別條路,
 * 所以本次才引進 `html-to-image`(純前端、體積小、走 SVG foreignObject
 * + 內聯 computed style,對本專案的 Tailwind 版面相容性比 html2canvas 好)。
 *
 * ⚠️ **JPEG 沒有分頁**。`@media print` 的分頁規則只在列印管線裡存在,
 * 截圖看到的就是螢幕版面 —— 多頁單據會變成一張很長的圖。這是本做法的固有限制,
 * 不要為了分頁去改列印樣式(那會反過來弄壞真正的 PDF 輸出)。
 */
import { toJpeg } from 'html-to-image';

/**
 * 截一個元素成 JPEG 並觸發下載。
 *
 * `pixelRatio: 2` 是為了讓小字(單據上有 0.6rem 的檔名列)放大看仍然清楚;
 * `backgroundColor` 要明確給白色 —— JPEG 沒有透明通道,不給的話透明處會變黑。
 *
 * ⚠️ **不要加 `cacheBust`**(REQ-20260903007)。稿樣圖走的是 MinIO 的 presigned
 * 網址(經 Caddy 反代,與前端同源,沒有 CORS 問題),而 `cacheBust` 的實作是把
 * 時間戳直接接到網址的 query 後面 —— 多一個未簽章的參數會讓 SigV4 驗簽失敗,
 * MinIO 回 403,整張匯出跟著失敗。有稿樣圖的訂單必爆,加回去等於把功能關掉。
 */
export async function exportNodeToJpeg(node: HTMLElement, filename: string): Promise<void> {
  const dataUrl = await toJpeg(node, {
    quality: 0.95,
    backgroundColor: '#ffffff',
    pixelRatio: 2,
  });
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `${filename}.jpg`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * 「這一份在畫面上看得見嗎」。
 *
 * 有些頁面同時掛著螢幕版與列印專用版兩份 DOM(帳款明細表就是),
 * 隱藏的那一份 `display:none`,截出來是一張空白圖 —— 要先濾掉。
 *
 * jsdom 沒有版面計算(`offsetParent` 永遠是 null、也沒有 `checkVisibility`),
 * 拿它判斷會讓測試環境一個目標都找不到。所以算不出來時一律當成看得見:
 * 測試環境本來就只渲染一份。
 */
function isVisible(el: HTMLElement): boolean {
  return typeof el.checkVisibility === 'function' ? el.checkVisibility() : true;
}

/**
 * 把 `root` 底下看得見的匯出目標逐一存成 JPEG。
 *
 * 一張就用 `baseFilename`,多張才加 `-1`、`-2` 後綴。
 * **逐張 `await`**,不要 `Promise.all` —— 瀏覽器會擋住短時間內的連續下載,
 * 平行送出只會有第一張存得下來。
 *
 * `selector` 預設是列印樣式共用的 `.print-area`;電子發票證明聯那頁的單據
 * 掛的是 `.cert`(它自帶一份 `PRINT_CSS`,不走全域的 `.print-area`)。
 */
export async function exportPrintAreasToJpeg(
  root: HTMLElement | null,
  baseFilename: string,
  selector = '.print-area',
): Promise<void> {
  const targets = Array.from(root?.querySelectorAll<HTMLElement>(selector) ?? []).filter(isVisible);
  if (targets.length === 0) {
    alert('找不到可以匯出的單據內容');
    return;
  }
  for (const [i, target] of targets.entries()) {
    const name = targets.length > 1 ? `${baseFilename}-${i + 1}` : baseFilename;
    await exportNodeToJpeg(target, name);
  }
}
